"use client";

import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { Reveal } from "@/components/ui/Reveal";
import { SectionLabel } from "@/components/ui/SectionLabel";
import { SplitHeading } from "@/components/ui/SplitHeading";
import { SECTION_TITLE_CLASS } from "@/lib/typography";
import type { Metric } from "@/lib/content/types";

type MetricsProps = {
  metrics: Metric[];
  label?: string;
  title?: string;
  description?: string;
};

function parseMetricValue(value: string): { target: number; suffix: string } {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)(.*)$/);
  if (!match) return { target: 0, suffix: value };
  return { target: Number(match[1]), suffix: match[2] ?? "" };
}

function AnimatedMetricValue({ value }: { value: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.05, margin: "0px 0px 25% 0px" });
  const { target, suffix } = parseMetricValue(value);
  // SSR + initial HTML use the real value so crawlers never see "0".
  const [display, setDisplay] = useState(target);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!inView || hasAnimated.current) return;
    hasAnimated.current = true;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setDisplay(target);
      return;
    }

    setDisplay(0);
    const duration = 1400;
    const start = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(target * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, target]);

  const label = `${target}${suffix}`;

  return (
    <p
      ref={ref}
      className="text-5xl font-medium tracking-tight tabular-nums md:text-6xl lg:text-7xl"
      aria-label={label}
    >
      {display}
      {suffix}
    </p>
  );
}

export function Metrics({
  metrics,
  label,
  title,
  description,
}: MetricsProps) {
  if (!metrics.length) return null;

  return (
    <section id="metrics" className="py-20 md:py-28 lg:py-32">
      <Container>
        <Reveal>
          <header className="mb-12 max-w-2xl md:mb-16">
            {label ? <SectionLabel className="mb-5">{label}</SectionLabel> : null}
            {title ? (
              <SplitHeading className={SECTION_TITLE_CLASS}>
                {title}
              </SplitHeading>
            ) : null}
            {description ? (
              <p className="mt-6 text-base leading-relaxed text-muted md:text-lg">
                {description}
              </p>
            ) : null}
          </header>
        </Reveal>

        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
          {metrics.map((metric, index) => (
            <Reveal key={metric.label} delay={index * 0.08}>
              <div
                className={`lg:px-8 ${
                  index === 0 ? "lg:pl-0" : ""
                } ${
                  index > 0
                    ? "lg:border-l lg:border-white/[0.1]"
                    : ""
                }`}
              >
                <AnimatedMetricValue value={metric.value} />
                <p className="mt-4 text-xs uppercase tracking-[0.18em] text-muted">
                  {metric.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}
